import i18next, { type i18n } from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';
import tr from './locales/tr.json';

/**
 * App language only controls words. Number/currency/date formatting comes
 * from the billing region (see format.ts). English is the source language;
 * every other locale must have exactly the same keys (enforced by a test).
 */
export const LANGUAGES = [
  { code: 'en', nativeName: 'English' },
  { code: 'tr', nativeName: 'Türkçe' },
] as const;

export type Language = (typeof LANGUAGES)[number]['code'];

export const resources = { en: { translation: en }, tr: { translation: tr } } as const;

export function isLanguage(value: string | undefined): value is Language {
  return LANGUAGES.some((l) => l.code === value);
}

export function detectLanguage(preferred: readonly string[] = globalThis.navigator?.languages ?? []): Language {
  for (const tag of preferred) {
    const base = tag.toLowerCase().split('-')[0];
    if (isLanguage(base)) return base;
  }
  return 'en';
}

/** Fresh instance per app (and per test) instead of the global singleton. */
export async function createI18n(language: Language): Promise<i18n> {
  const instance = i18next.createInstance();
  await instance.use(initReactI18next).init({
    resources,
    lng: language,
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
    returnNull: false,
  });
  return instance;
}
